import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { listGroups, listImportBatches, listRoles, onMembersChanged } from '../../../services';
import { buildImportBatchOptions } from '../utils/importBatchOptions';

const toOptions = (res) =>
  (res?.body?.items ?? []).map(({ id, name }) => ({ value: id, label: name }));

/**
 * Options of the Role, Group and Imported-batch filters (flat `{ value, label }`). A failure leaves
 * a list empty: the filter just has nothing to offer, the list itself is unaffected. Batches
 * refetch when members change (an import adds one).
 */
export const useMemberFilterOptions = () => {
  const { t, i18n } = useTranslation('people-access');
  const [lists, setLists] = useState({ roles: [], groups: [], batches: [] });

  const mountedRef = useRef(true);

  const loadBatches = useCallback(
    () =>
      listImportBatches()
        .then((res) => {
          if (mountedRef.current) setLists((prev) => ({ ...prev, batches: res?.body?.items ?? [] }));
        })
        .catch(() => {}),
    [],
  );

  useEffect(() => {
    mountedRef.current = true;

    Promise.all([listRoles(), listGroups()])
      .then(([roles, groups]) => {
        if (mountedRef.current) setLists((prev) => ({ ...prev, roles: toOptions(roles), groups: toOptions(groups) }));
      })
      .catch(() => {});
    loadBatches();
    const off = onMembersChanged(loadBatches);

    return () => {
      mountedRef.current = false;
      off();
    };
  }, [loadBatches]);

  const locale = i18n?.language;

  return useMemo(
    () => ({
      roles: lists.roles,
      groups: lists.groups,
      batches: buildImportBatchOptions(lists.batches, t, locale),
    }),
    [lists, t, locale],
  );
};
